import { Icon } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Table, TableMenu } from '@/components_deprecated'
import type { SalaryWorkTimeNode } from '@/modules/companies'
import { isMobileOnly } from 'react-device-detect'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import { useConfirm } from '@/utils/hooks'
import { useSalaryMutations } from '../../util'
import type { SomeOptional } from '@/types/util'

const Arrows = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
`

const Arrow = styled(Icon)`
  cursor: pointer;
`

const ReorderTableCell = styled.td`
  width: 1px;
  padding: 0 0 0 1rem !important;
`

type SalaryWorkTimeItem = SomeOptional<
  Pick<
    SalaryWorkTimeNode,
    'id' | 'user' | 'name' | 'salaryType' | 'salaryDateStart' | 'salaryDateEnd'
  >,
  'user'
>

interface SalaryWorkTimesTableProps {
  salaryWorkTimes: SalaryWorkTimeItem[]
}

export const SalaryWorkTimesTables: React.FC<SalaryWorkTimesTableProps> = ({
  salaryWorkTimes,
}) => {
  const translations = useTranslate({
    info: 'settings.info.salary',
    salarySettings: 'settings.salary-and-work-time',
    salaryType: 'settings.salary-type',

    defaultSalaryCode: 'settings.default-salary-code',

    module: 'common.module',
    name: 'common.name',
    dailyLimit: 'settings.daily-work-limit-short',
    weeklyLimit: 'settings.period-work-limit-short',
    pauseDuration: 'settings.pause-duration-short',
    pauseThreshold: 'settings.pause-threshold-short',
    salaryPeriod: 'settings.period',
    prompt: {
      deleteTitle: 'settings.prompts.salary-setting-delete-title',
      delete: ['settings.prompts.delete', { name: '' }],
    },

    priority: 'common.priority',

    delete: 'common.delete-alt',
    edit: 'common.edit',
    add: 'common.add',
    departments: 'common.departments',

    salaryTypes: {
      FIXED: 'settings.fixed-salary',
      HOUR: 'settings.hourly-salary',
      DAY: 'settings.daily-salary',
    },
  })

  const navigate = useNavigate()

  const confirm = useConfirm()
  const mutations = useSalaryMutations()

  async function handleDelete(salaryWorkTime: SalaryWorkTimeItem) {
    const { data: answer } = await confirm(translations.prompt.deleteTitle)
    if (!answer) return

    void mutations.delete({
      variables: {
        id: salaryWorkTime.id,
      },
    })
  }

  return (
    <Table noShadow noBorders noHeaderMargin noRowMargin>
      <thead>
        <tr>
          {salaryWorkTimes.length > 1 && (
            <th className="center" colSpan={2}>
              {translations.priority}
            </th>
          )}
          <th className="center">{translations.name}</th>
          <th className="center">{translations.salaryType}</th>

          {!isMobileOnly && (
            <>
              <th className="center">{translations.salaryPeriod}</th>
            </>
          )}
          <th></th>
        </tr>
      </thead>

      <tbody>
        {salaryWorkTimes.map((salaryWorkTime, i) => {
          const goToEditPage = () =>
            salaryWorkTime.user?.id
              ? void navigate(
                  `/users/${salaryWorkTime.user?.id}/salary/${salaryWorkTime.id}`
                )
              : void navigate('/settings/salary/' + salaryWorkTime.id)

          return (
            <tr key={salaryWorkTime.id}>
              {salaryWorkTimes.length > 1 && (
                <>
                  <ReorderTableCell>
                    <Arrows>
                      {i !== 0 && (
                        <Arrow
                          icon="chevron-up"
                          hoverColor="secondary"
                          margin="0 0 4px 0"
                          onClick={() => {
                            const first = salaryWorkTime.id
                            const second = salaryWorkTimes[i - 1]?.id
                            if (!first || !second) return

                            return mutations.swap({
                              variables: {
                                input: {
                                  first,
                                  second,
                                },
                              },
                            })
                          }}
                        />
                      )}

                      {i !== salaryWorkTimes.length - 1 && (
                        <Arrow
                          icon="chevron-down"
                          hoverColor="secondary"
                          margin="0 0 4px 0"
                          onClick={() => {
                            const first = salaryWorkTime.id
                            const second = salaryWorkTimes[i - 1]?.id
                            if (!first || !second) return

                            return mutations.swap({
                              variables: {
                                input: {
                                  first,
                                  second,
                                },
                              },
                            })
                          }}
                        />
                      )}
                    </Arrows>
                  </ReorderTableCell>

                  <td className="center">{i + 1}</td>
                </>
              )}

              <td className="center">{salaryWorkTime.name}</td>

              <td className="center">
                {translations.salaryTypes[salaryWorkTime.salaryType]}
              </td>

              {!isMobileOnly && (
                <>
                  <td className="center">
                    {salaryWorkTime.salaryDateStart}.&nbsp;-&nbsp;
                    {salaryWorkTime.salaryDateEnd}.
                  </td>
                </>
              )}

              <TableMenu
                items={[
                  {
                    label: translations.edit,
                    onClick: () => goToEditPage(),
                  },
                  {
                    label: translations.delete,
                    onClick: () => handleDelete(salaryWorkTime),
                  },
                ]}
              />
            </tr>
          )
        })}
      </tbody>
    </Table>
  )
}
